import React, { useState, useEffect, useRef } from 'react';
import PhotoTextInput from './PhotoTextInput';
import CatalogSelect from './CatalogSelect';
import BottomNav from './BottomNav';
import { loadCustomTexts, deleteCustomText, saveCustomText, updateCustomText } from '../utils/storage';
import { pushCustomTextsToCloud } from '../services/customTextsApi';
import BUILTIN_TEXTS from '../data/builtinTexts';
import { trackEvent } from '../utils/analytics';
import { getRemainingAIScore, canUseAIScore, canUseAIScoreOrCredit, canUsePhoto, incrementPhotoUsage, getRemainingPhoto } from '../utils/premiumGate';
import PremiumPrompt from './PremiumPrompt';
import { fetchChildAssignments } from '../services/assignmentApi';

const PUBLISHERS = [
  { value: 'all',       label: '全て' },
  { value: 'mitsumura', label: '光村図書' },
  { value: 'tosho',     label: '東京書籍' },
  { value: 'kyoiku',    label: '教育出版' },
];

const GRADES = [
  { value: 0, label: '全学年' },
  { value: 1, label: '1年' },
  { value: 2, label: '2年' },
  { value: 3, label: '3年' },
  { value: 4, label: '古典' },
];

const CARD_ICONS = {
  'でんでんむしむし': '🐌',
  'どんぐりころころ': '🌰',
  'もしもしかめよ':   '🐢',
  'ちょうちょう':     '🦋',
  'ももたろう':       '🍑',
  'おむすびころりん': '🍙',
  'さるかにがっせん': '🦀',
  'こぶとりじいさん': '👴',
  '竹取物語':         '🎋',
  '徒然草（序段）':   '📜',
  '枕草子（春はあけぼの）': '🌸',
  '平家物語（冒頭）': '⚔️',
};

const BUILTIN_COLORS = ['var(--c1)', 'var(--c2)', 'var(--c3)', 'var(--c4)', 'var(--c1)', 'var(--c2)', 'var(--c3)', 'var(--c4)'];
const BUILTIN_INITIAL_COUNT = 4;

export default function TextSelect({ onSelect, onBack, onNav, initialSelected, isPremium, aiCredits, currentChild, onUpgrade, familyId, isGuest, onBuyCredits, onAssignment }) {
  const [publisher, setPublisher]     = useState('all');
  const [grade, setGrade]             = useState(0);
  const [customTexts, setCustomTexts] = useState([]);
  const [showPhoto,    setShowPhoto]   = useState(false);
  const [showManual,   setShowManual]  = useState(false);
  const [showCatalog,  setShowCatalog] = useState(false);
  const [manualTitle,  setManualTitle] = useState('');
  const [manualBody,   setManualBody]  = useState('');
  const [editingText,  setEditingText] = useState(null); // 編集中のカスタムテキスト
  const [selectedText, setSelectedText] = useState(initialSelected ?? null);
  const [showAllBuiltin, setShowAllBuiltin] = useState(false);
  const [showPremiumPrompt, setShowPremiumPrompt] = useState(false);
  const [showPhotoGuestPrompt, setShowPhotoGuestPrompt] = useState(false);
  const [showGuestTextLimit, setShowGuestTextLimit] = useState(false);
  const [assignments, setAssignments] = useState([]); // 先生からの課題（ログイン中の子のみ）

  const GUEST_TEXT_LIMIT = 5;

  const previewRef = useRef(null);

  // 先生からの課題を取得（ゲストは配布リンクから直接使うのでここには出さない）
  useEffect(() => {
    if (isGuest || !currentChild?.id) { setAssignments([]); return; }
    fetchChildAssignments(currentChild.id).then(setAssignments);
  }, [isGuest, currentChild?.id]);

  useEffect(() => {
    const texts = loadCustomTexts();
    setCustomTexts(texts);
    // ゲストで5件超えバナーが表示される場合を計測
    if (isGuest && texts.length > GUEST_TEXT_LIMIT) {
      trackEvent('ondoku_guest_text_limit_banner', { count: texts.length });
    }
    // マウント時に1回だけ実行する意図。isGuest を依存に入れると
    // ログイン状態が変わるたびに計測イベントが二重に飛ぶ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // カード選択時に下のプレビューへスクロール
  useEffect(() => {
    if (selectedText && previewRef.current) {
      setTimeout(() => {
        previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  }, [selectedText]);

  const filteredBuiltin = BUILTIN_TEXTS.filter((t) => {
    if (publisher !== 'all' && t.publisher !== publisher) return false;
    if (grade !== 0 && t.grade !== grade) return false;
    return true;
  });


  const handleEditCustom = (text, e) => {
    e.stopPropagation();
    // paragraphs → 1行ずつのテキストに戻す
    const body = text.paragraphs?.flat().join('\n') ?? '';
    setEditingText(text);
    setManualTitle(text.title ?? '');
    setManualBody(body);
    setShowManual(true);
  };

  const handleManualSave = () => {
    const lines = manualBody.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
    if (lines.length === 0) return;
    const paragraphs = [];
    for (let i = 0; i < lines.length; i += 3) paragraphs.push(lines.slice(i, i + 3));
    const title = manualTitle.trim() || 'じぶんのテキスト';

    // ゲストの新規保存が上限に達している場合はモーダルを表示
    if (isGuest && !editingText && customTexts.length >= GUEST_TEXT_LIMIT) {
      setShowManual(false);
      setShowGuestTextLimit(true);
      return;
    }

    let saved;
    if (editingText) {
      // 編集モード：既存を上書き
      saved = updateCustomText(editingText.id, { title, author: '', paragraphs });
    } else {
      // 新規作成モード
      saved = saveCustomText({ title, author: '', paragraphs });
    }
    trackEvent('ondoku_custom_text_saved', { is_new: !editingText, total_count: loadCustomTexts().length });
    setCustomTexts(loadCustomTexts());
    pushCustomTextsToCloud(familyId); // クラウド同期（ログイン時のみ有効）
    setSelectedText(saved);
    setShowManual(false);
    setEditingText(null);
    setManualTitle('');
    setManualBody('');
  };

  if (showCatalog) {
    return (
      <CatalogSelect
        onSelect={(savedText) => {
          setCustomTexts(loadCustomTexts());
          pushCustomTextsToCloud(familyId);
          setSelectedText(savedText);
          setShowCatalog(false);
        }}
        onBack={() => setShowCatalog(false)}
      />
    );
  }

  if (showPhoto) {
    return (
      <PhotoTextInput
        onTextExtracted={(savedText) => {
          incrementPhotoUsage();
          setCustomTexts(loadCustomTexts());
          pushCustomTextsToCloud(familyId);
          setSelectedText(savedText);
          setShowPhoto(false);
        }}
        onCancel={() => setShowPhoto(false)}
      />
    );
  }

  if (showManual) {
    return (
      <div className="screen manual-input-screen">
        <button className="btn-back" onClick={() => {
          setShowManual(false);
          setEditingText(null);
          setManualTitle('');
          setManualBody('');
        }}>← もどる</button>
        <h3 className="manual-title-head">
          {editingText ? '✏️ テキストを　へんしゅう' : '✏️ じぶんで　にゅうりょく'}
        </h3>
        <p className="manual-sub">タイトルと　よむ　ぶんしょうを　いれてね</p>
        <div className="manual-form">
          <label className="manual-label">タイトル</label>
          <input
            className="title-input"
            type="text"
            placeholder="かぜのゆうびんやさん　など"
            value={manualTitle}
            onChange={(e) => setManualTitle(e.target.value)}
          />
          <label className="manual-label" style={{ marginTop: 16 }}>ぶんしょう</label>
          <p className="manual-hint">1ぎょうずつ　かいてね。3ぎょうで　1だんらくになるよ。</p>
          <textarea
            className="extracted-text-area"
            placeholder={'かぜが　ふいている。\nゆうびんやさんが　はしる。\nてがみを　とどける。'}
            value={manualBody}
            onChange={(e) => setManualBody(e.target.value)}
            rows={10}
          />
        </div>
        <button
          className="btn-next"
          style={{ marginTop: 16, width: 'auto', alignSelf: 'center', padding: '4px 32px', flex: 'none' }}
          onClick={handleManualSave}
          disabled={manualBody.trim().length === 0}
        >{editingText ? '✅ こうしんする' : '✅ ほぞんする'}</button>

        {/* 削除ボタン（編集モードのみ） */}
        {editingText && (
          <button
            className="btn-delete-text"
            onClick={() => {
              if (!window.confirm('このテキストを　けしますか？')) return;
              deleteCustomText(editingText.id);
              setCustomTexts(loadCustomTexts());
              pushCustomTextsToCloud(familyId);
              if (selectedText?.id === editingText.id) setSelectedText(null);
              setShowManual(false);
              setEditingText(null);
              setManualTitle('');
              setManualBody('');
            }}
          >
            🗑️ このテキストを　さくじょする
          </button>
        )}
      </div>
    );
  }

  const fullPreview = selectedText?.paragraphs?.flat().join('　') ?? '';
  const paraCount   = selectedText?.paragraphs?.length ?? 1;

  return (
    <div className="screen text-select-screen" style={{ padding: 0, gap: 0 }}>

      {/* ─── ヘッダー ─── */}
      <div className="v9-header" style={{ width: '100%', flexShrink: 0 }}>
        <div className="v9-blob1" />
        <div className="v9-blob2" />
        <div className="v9-header-inner">
          <div className="v9-header-top">
            <div>
              <div className="v9-logo-sub">READING APP</div>
              <div className="v9-logo-main">Ondoku <span>Quest</span></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {currentChild && (
                <span style={{ color: 'white', fontSize: 16, fontWeight: 700, opacity: 0.9 }}>
                  {currentChild.avatar} {currentChild.name}
                </span>
              )}
              <button
                onClick={onBack}
                style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 24, color: 'white', fontSize: 13, fontWeight: 700, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit' }}
              >← ホーム</button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── スクロールエリア ─── */}
      <div className="text-select-scroll">

      {/* ─── ほんをえらぼう ─── */}
      <div className="v9-content" style={{ width: '100%' }}>

        {/* 先生からの課題（最上部・期限内のみ） */}
        {assignments.length > 0 && (
          <>
            <div className="v9-sec-label">
              <span className="v9-sec-label-icon">🏫</span>
              <span className="v9-sec-label-text">せんせいからの　かだい</span>
              <div className="v9-sec-label-line" />
            </div>
            <div className="assign-inbox">
              {assignments.map((a) => (
                <button
                  key={a.id}
                  className="assign-inbox-card"
                  onClick={() => onAssignment && onAssignment(a.id)}
                >
                  <span className="assign-inbox-icon">📩</span>
                  <span className="assign-inbox-body">
                    <span className="assign-inbox-title">{a.title || 'せんせいの　かだい'}</span>
                    <span className="assign-inbox-meta">
                      {new Date(a.expires_at).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}
                      まで
                    </span>
                  </span>
                  <span className="assign-inbox-arrow">›</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* セクションラベル */}
        <div className="v9-sec-label">
          <span className="v9-sec-label-icon">📚</span>
          <span className="v9-sec-label-text">ほんをえらぼう</span>
          <div className="v9-sec-label-line" />
        </div>

        {/* フィルター */}
        <div className="filter-row">
          {PUBLISHERS.map((p) => (
            <button key={p.value} className={`filter-btn ${publisher === p.value ? 'active' : ''}`}
              onClick={() => setPublisher(p.value)}>{p.label}</button>
          ))}
        </div>
        <div className="filter-row" style={{ marginBottom: 0 }}>
          {GRADES.map((g) => (
            <button key={g.value} className={`filter-btn ${grade === g.value ? 'active' : ''}`}
              onClick={() => setGrade(g.value)}>{g.label}</button>
          ))}
        </div>

        {/* ゲスト5件超えバナー（グリッドの外・横幅いっぱい） */}
        {isGuest && customTexts.length > GUEST_TEXT_LIMIT && (
          <div style={{
            background: '#fff7ed', border: '1.5px solid #f97316', borderRadius: 12,
            padding: '10px 14px', margin: '8px 0', fontSize: 13, color: '#c2410c',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span>⚠️</span>
            <span style={{ flex: 1 }}>
              テキストが{GUEST_TEXT_LIMIT}件を超えています。ログインするか、いくつか削除してください。
            </span>
            <button
              onClick={() => onNav('auth')}
              style={{ flexShrink: 0, background: '#f97316', color: 'white', border: 'none', borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 700, fontFamily: 'var(--font)', cursor: 'pointer' }}
            >ログイン</button>
          </div>
        )}

        {/* 本カードグリッド */}
        <div className="books-grid">

          {/* 内蔵テキスト */}
          {(showAllBuiltin ? filteredBuiltin : filteredBuiltin.slice(0, BUILTIN_INITIAL_COUNT)).map((t, i) => (
            <div
              key={t.id}
              className={`book-card${selectedText?.id === t.id ? ' selected' : ''}`}
              style={{ background: BUILTIN_COLORS[i % BUILTIN_COLORS.length] }}
              onClick={() => setSelectedText(selectedText?.id === t.id ? null : t)}
            >
              <div className="card-icon-wrap">{CARD_ICONS[t.title] ?? '📖'}</div>
              <div className="card-badge">みほん</div>
              <div className="card-title">『{t.title}』</div>
              <div className="card-author">{t.author || `${t.grade}年`}</div>
            </div>
          ))}

          {/* もっとみるボタン */}
          {!showAllBuiltin && filteredBuiltin.length > BUILTIN_INITIAL_COUNT && (
            <div
              className="book-card book-card-more"
              onClick={() => setShowAllBuiltin(true)}
            >
              <div className="card-icon-wrap">📚</div>
              <div className="card-title">もっとみる</div>
              <div className="card-author">あと{filteredBuiltin.length - BUILTIN_INITIAL_COUNT}さくひん</div>
            </div>
          )}

          {/* カスタムテキスト */}
          {customTexts.map((t) => (
            <div
              key={t.id}
              className={`book-card${selectedText?.id === t.id ? ' selected' : ''}`}
              style={{ background: 'var(--c5)' }}
              onClick={() => setSelectedText(selectedText?.id === t.id ? null : t)}
            >
              <div className="card-icon-wrap">📱</div>
              <button className="btn-edit-custom" onClick={(e) => handleEditCustom(t, e)}>✏️</button>
              <div className="card-badge">じぶん</div>
              <div className="card-title">『{t.title}』</div>
              <div className="card-author">このきじゅん</div>
            </div>
          ))}

          {/* しゃしん（ベータ：ログイン済みのみ） */}
          {isGuest ? (
            <div className="book-card" style={{ background: 'var(--c6)', opacity: 0.85 }} onClick={() => setShowPhotoGuestPrompt(true)}>
              <div className="card-icon-wrap">📷</div>
              <div className="card-badge">β ベータ</div>
              <div className="card-title">しゃしんをとる</div>
              <div className="card-author">ログインして使う</div>
            </div>
          ) : canUsePhoto(isPremium) ? (
            <div className="book-card" style={{ background: 'var(--c6)' }} onClick={() => setShowPhoto(true)}>
              <div className="card-icon-wrap">📷</div>
              <div className="card-badge">β ベータ</div>
              <div className="card-title">しゃしんをとる</div>
              {!isPremium && <div className="card-author">今日あと{getRemainingPhoto()}まい</div>}
            </div>
          ) : (
            <div className="book-card book-card-disabled" style={{ background: 'var(--c6)' }}>
              <div className="card-icon-wrap">📷</div>
              <div className="card-badge">β ベータ</div>
              <div className="card-title">しゃしんをとる</div>
              <div className="card-author">今日は3まい使いました</div>
            </div>
          )}

        </div>

        {/* ─── 下段：横幅いっぱいの2枚カード ─── */}
        <div className="action-cards-row">

          {/* 教科書から選ぶ */}
          <div className="book-card action-card" style={{ background: 'var(--c5)' }} onClick={() => setShowCatalog(true)}>
            <div className="card-icon-wrap">📚</div>
            <div className="card-badge">えらぶ</div>
            <div className="card-title">教科書から選ぶ</div>
            <div className="card-author">学年・出版社</div>
          </div>

          {/* 自分で入力 */}
          <div className="book-card action-card" style={{ background: 'var(--c5)' }} onClick={() => setShowManual(true)}>
            <div className="card-icon-wrap">✏️</div>
            <div className="card-badge">にゅうりょく</div>
            <div className="card-title">自分で入力</div>
            <div className="card-author">テキストをうつ</div>
          </div>

        </div>
      </div>

      {/* ─── よむ文章（カード選択時のみ表示） ─── */}
      {selectedText && (
        <div ref={previewRef} className="v9-content-muted" style={{ width: '100%' }}>

          <div className="v9-sec-label" style={{ paddingTop: 4 }}>
            <span className="v9-sec-label-icon">📖</span>
            <span className="v9-sec-label-text">よむ文章</span>
            <div className="v9-sec-label-line" />
          </div>

          <div className="v9-text-card" style={{ margin: '0 0 14px' }}>
            <div className="v9-text-meta">
              <div className="v9-text-book-badge">『{selectedText.title}』</div>
              <div className="v9-text-para-badge">{paraCount}だんらく</div>
            </div>
            <div className="v9-text-body">{fullPreview}</div>
            <div className="v9-text-footer">
              <div className="v9-text-footer-info">
                📖 {selectedText.author || 'オリジナル'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(124,58,237,0.4)' }}>
                ぜんぶで {paraCount}だんらく
              </div>
            </div>
          </div>

          <div className="v9-action-btns">
            <button className="v9-action-btn v9-btn-practice" onClick={() => {
              const method = selectedText.id?.startsWith('custom_') ? 'custom'
                : BUILTIN_TEXTS.some(t => t.id === selectedText.id) ? 'builtin'
                : selectedText.id ? 'catalog' : 'manual';
              trackEvent('ondoku_text_selected', { method, text_title: selectedText.title || '（タイトルなし）' });
              onSelect('renshyu', selectedText);
            }}>
              <div className="v9-btn-icon">📖</div>
              <div className="v9-btn-title">れんしゅう</div>
              <div className="v9-btn-sub">だんらくずつ</div>
            </button>
            <button className="v9-action-btn v9-btn-real" onClick={() => {
              if (!canUseAIScoreOrCredit(isPremium, aiCredits, isGuest)) {
                trackEvent('ondoku_daily_limit_reached', { is_premium: isPremium, is_guest: isGuest });
                setShowPremiumPrompt(true);
                return;
              }
              const method = selectedText.id?.startsWith('custom_') ? 'custom'
                : BUILTIN_TEXTS.some(t => t.id === selectedText.id) ? 'builtin'
                : selectedText.id ? 'catalog' : 'manual';
              trackEvent('ondoku_text_selected', { method, text_title: selectedText.title || '（タイトルなし）' });
              onSelect('honban', selectedText);
            }}>
              <div className="v9-btn-icon">🎯</div>
              <div className="v9-btn-title">ほんばん</div>
              <div className="v9-btn-sub">とおしてよむ</div>
            </button>
          </div>

          {/* AI採点 残り回数 */}
          <div className="ai-score-counter">
            <span className="ai-score-counter-icon">🤖</span>
            <span className="ai-score-counter-label">ほんばん採点</span>
            {canUseAIScore(isPremium, isGuest) ? (
              <>
                <span className="ai-score-counter-remain">
                  {isGuest ? 'のこり' : '今日あと'}
                  <strong>{getRemainingAIScore(isPremium, isGuest)}</strong>
                  <span className="unit">回</span>
                </span>
                {aiCredits > 0 && (
                  <span className="ai-score-counter-credits">+{aiCredits}回</span>
                )}
                <span className="ai-score-counter-plan">
                  {isPremium ? '👑 プレミアム' : isGuest ? 'ゲスト' : '無料'}
                </span>
              </>
            ) : aiCredits > 0 ? (
              // 日次上限超えたがクレジット残あり
              <span className="ai-score-counter-remain">
                クレジット
                <strong>{aiCredits}</strong>
                <span className="unit">回</span>
              </span>
            ) : (
              // 上限超え・クレジットなし
              <span className="ai-score-counter-upgrade">
                {isGuest ? 'ためせるのはここまでです' : '今日の上限です'}
                {isPremium ? (
                  // プレミアム：クレジット購入ボタン
                  <span className="ai-score-buy-btns">
                    <button className="ai-score-buy-btn" onClick={() => onBuyCredits('5')}>
                      +5回 ¥120
                    </button>
                    <button className="ai-score-buy-btn" onClick={() => onBuyCredits('10')}>
                      +10回 ¥200
                    </button>
                  </span>
                ) : (
                  // 無料：プレミアムへ誘導 + クレジット購入
                  <span className="ai-score-buy-btns">
                    <button className="ai-score-upgrade-btn" onClick={onUpgrade}>
                      👑 プレミアムにしよう
                    </button>
                    <button className="ai-score-buy-btn" onClick={() => onBuyCredits('5')}>
                      +5回 ¥120
                    </button>
                    <button className="ai-score-buy-btn" onClick={() => onBuyCredits('10')}>
                      +10回 ¥200
                    </button>
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      )}

      </div>{/* text-select-scroll end */}

      <BottomNav active="textSelect" onNav={onNav} />

      {showPremiumPrompt && (
        <PremiumPrompt
          isPremium={isPremium}
          isGuest={isGuest}
          onClose={() => setShowPremiumPrompt(false)}
          onUpgrade={onUpgrade}
        />
      )}

      {showGuestTextLimit && (
        <div className="credit-login-overlay" onClick={() => setShowGuestTextLimit(false)}>
          <div className="credit-login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="credit-login-icon">📦</div>
            <div className="credit-login-title">テキストが{GUEST_TEXT_LIMIT}件いっぱいです</div>
            <div className="credit-login-body">
              ログインすると無制限に保存できるよ。<br/>
              または、使っていないテキストを削除してね。
            </div>
            <div className="credit-login-btns">
              <button className="credit-login-btn-primary" onClick={() => { setShowGuestTextLimit(false); onNav('auth'); }}>
                ログイン / 新規登録（無料）
              </button>
              <button className="credit-login-btn-secondary" onClick={() => setShowGuestTextLimit(false)}>
                テキストを削除して整理する
              </button>
              <button className="credit-login-btn-cancel" onClick={() => setShowGuestTextLimit(false)}>
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      {showPhotoGuestPrompt && (
        <div className="credit-login-overlay" onClick={() => setShowPhotoGuestPrompt(false)}>
          <div className="credit-login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="credit-login-icon">📷</div>
            <div className="credit-login-title">ログインすると<br/>しゃしんをとる機能が使えるよ！</div>
            <div className="credit-login-body">無料でアカウントを作れます</div>
            <div className="credit-login-btns">
              <button className="credit-login-btn-primary" onClick={() => { setShowPhotoGuestPrompt(false); onNav('auth'); }}>
                ログイン / 新規登録
              </button>
              <button className="credit-login-btn-cancel" onClick={() => setShowPhotoGuestPrompt(false)}>
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
